import React from "react";
import { useLocation } from "react-router-dom";
import { Text, Card, Flex } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { WorkflowStep, StepStatus } from "../../constants";
import { WorkflowStepConfig, ImportProgressProps } from "../../types";
import { getCurrentStepFromPath, getImportProgressStepStatus } from "../../utils/workflowUtils";
import * as styles from "./styles_vanilla.css";

const ImportProgress: React.FC<ImportProgressProps> = () => {
  const location = useLocation();

  // Define step order for status calculation
  const stepOrder = [
    WorkflowStep.PROJECT_SETUP,
    WorkflowStep.DATA_IMPORT,
    WorkflowStep.DATA_VALIDATION,
    WorkflowStep.TEST_DATA_RELEASE,
  ];

  const currentStep = getCurrentStepFromPath(location.pathname);

  const getStepStatus = (stepId: WorkflowStep): StepStatus => {
    return getImportProgressStepStatus(stepId, currentStep, stepOrder);
  };

  const steps: WorkflowStepConfig[] = [
    {
      id: WorkflowStep.PROJECT_SETUP,
      title: "Project Setup",
      subtitle: "Configure project details",
      icon: "building",
      status: getStepStatus(WorkflowStep.PROJECT_SETUP),
    },
    {
      id: WorkflowStep.DATA_IMPORT,
      title: "Data Import",
      subtitle: "Upload files to import",
      icon: "upload",
      status: getStepStatus(WorkflowStep.DATA_IMPORT),
    },
    {
      id: WorkflowStep.DATA_VALIDATION,
      title: "Data Validation",
      subtitle: "Verify data integrity",
      icon: "exclamation-triangle",
      status: getStepStatus(WorkflowStep.DATA_VALIDATION),
    },
    {
      id: WorkflowStep.TEST_DATA_RELEASE,
      title: "Test Data Release",
      subtitle: "Schedule import",
      icon: "calendar-alt",
      status: getStepStatus(WorkflowStep.TEST_DATA_RELEASE),
    },
  ];

  const getStepIconColor = (status: StepStatus) => {
    switch (status) {
      case StepStatus.COMPLETED:
        return Colors.Neutral0; // White icon on black background
      case StepStatus.ACTIVE:
        return Colors.Neutral0; // White icon on black background
      default:
        return Colors.Neutral600;
    }
  };

  return (
    <Card className={styles.progressContainer}>
      <Text variant="h2" color={Colors.Neutral900} className={styles.sectionTitle}>
        Import Progress
      </Text>

      <Flex className={styles.stepsContainer}>
        {steps.map((step, index) => {
          const isLastStep = index >= steps.length - 1;
          const baseStepCardClass = isLastStep ? styles.stepCard : styles.stepCardWithLine;

          // All steps are non-clickable - users must navigate through form submissions
          const stepCardClass = baseStepCardClass;

          return (
            <Flex
              key={step.id}
              flexDirection="column"
              alignItems="center"
              className={stepCardClass}
            >
              <Flex
                className={`${styles.stepIcon} ${
                  step.status === StepStatus.COMPLETED
                    ? styles.stepIconCompleted
                    : step.status === StepStatus.ACTIVE
                      ? styles.stepIconActive
                      : styles.stepIconPending
                }`}
                alignItems="center"
                justifyContent="center"
              >
                <Icon type={step.icon} size="large" color={getStepIconColor(step.status)} />
              </Flex>
              <Flex flexDirection="column" alignItems="center" className={styles.stepText}>
                <Text
                  variant={step.status === StepStatus.ACTIVE ? "strong-md" : "strong-sm"}
                  color={
                    step.status === StepStatus.PENDING ? Colors.Neutral400 : Colors.Neutral900
                  }
                  className={styles.stepTitle}
                >
                  {step.title}
                </Text>
                <Text variant="regular-sm" className={styles.stepSubtitle}>
                  {step.subtitle}
                </Text>
                {step.status === StepStatus.ACTIVE && (
                  <Text variant="regular-sm" className={styles.currentIndicator}>
                    Current
                  </Text>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
};

export default ImportProgress;
